import * as React from "react";
import { Link } from "react-router-dom";
import { Loader2, MessageSquareWarning } from "lucide-react";
import { DESCRIPTION_MAX, githubRepoKey } from "@mycompbox/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FixTargetPicker } from "@/components/pull-requests/fix-target-picker";
import { apiErrorInfo } from "@/components/pull-requests/pull-request-status";
import { useReportEpicGap } from "@/hooks/queries/use-epics";
import { REPORT_GAP_FINDING_MAX, type ReportGapResult } from "@/lib/api/epics";
import type { FixJobTarget } from "@/lib/api/pull-requests";
import type { Epic } from "@/lib/api/types";

export interface ReportGapFeature {
  id: string;
  identifier?: string | null;
  title: string;
}

interface FieldErrors {
  featureId?: string;
  description?: string;
  finding?: string;
}

/** Client-side mirror of the API's reportGapSchema (minus the target). */
export function validateReportGap(input: {
  featureId: string;
  description: string;
  finding: string;
}): FieldErrors {
  const errors: FieldErrors = {};
  if (!input.featureId) errors.featureId = "Choose the feature this belongs to.";
  const description = input.description.trim();
  if (description.length === 0) errors.description = "Describe what's missing.";
  else if (description.length > DESCRIPTION_MAX) errors.description = "That description is too long.";
  if (input.finding.trim().length > REPORT_GAP_FINDING_MAX) {
    errors.finding = `Keep the finding under ${String(REPORT_GAP_FINDING_MAX)} characters.`;
  }
  return errors;
}

/** Whether the "Report a gap" action applies: only to implemented, non-archived epics. */
export function canReportGap(epic: Pick<Epic, "state" | "isArchived">): boolean {
  return epic.state === "implemented" && !epic.isArchived;
}

function ResultSummary({ result }: { result: ReportGapResult }) {
  const taskLabel = result.task.identifier || result.task.title;
  return (
    <div role="status" className="space-y-2 text-sm">
      <p>
        Created follow-up task <span className="font-medium">{taskLabel}</span>.
      </p>
      {result.build.on === "pull_request_branch" ? (
        <p>
          The build is queued on the open pull request's branch{" "}
          <code className="text-xs">{result.build.branch}</code>.
        </p>
      ) : (
        <p>The build is queued on a new follow-up branch.</p>
      )}
      <Link to={`/mailbox/jobs/${result.job.id}`} className="text-primary hover:underline">
        View the build
      </Link>
    </div>
  );
}

/**
 * ENG-328: on an implemented epic, report what it is missing. Creates a
 * follow-up task under the chosen feature and queues a build. Renders nothing
 * when the epic isn't implemented.
 */
export function ReportGapDialog({
  epic,
  features,
  label = "Report a gap",
  triggerClassName,
  initialFeatureId,
  initialDescription,
  open: openProp,
  onOpenChange,
}: {
  epic: Epic;
  features: ReportGapFeature[];
  /** The trigger's words (the new look says "Something's missing"). */
  label?: string;
  triggerClassName?: string;
  /** ENG-354: pre-select this feature when it belongs to the epic. */
  initialFeatureId?: string | null;
  /** ENG-354: pre-fill the findings text (truncated to REPORT_GAP_FINDING_MAX). */
  initialDescription?: string | null;
  /** ENG-355: control the dialog from outside (e.g. the wrap-up banner's "Report a gap"). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;
  const [featureId, setFeatureId] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [finding, setFinding] = React.useState("");
  const [target, setTarget] = React.useState<FixJobTarget | null>(null);
  const [errors, setErrors] = React.useState<FieldErrors>({});
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<ReportGapResult | null>(null);
  const reportGap = useReportEpicGap(epic.id);

  // With a single feature there's nothing to choose.
  const onlyFeatureId = features.length === 1 ? features[0]?.id ?? "" : "";
  // ENG-354: a caller-supplied feature wins when it is one of the epic's features.
  const presetFeatureId =
    initialFeatureId && features.some((f) => f.id === initialFeatureId) ? initialFeatureId : onlyFeatureId;
  const presetFinding = (initialDescription ?? "").slice(0, REPORT_GAP_FINDING_MAX);
  React.useEffect(() => {
    if (open && !featureId && presetFeatureId) setFeatureId(presetFeatureId);
  }, [open, featureId, presetFeatureId]);

  // ENG-354/355: re-seed the presets every time the dialog opens and clear it on close,
  // whether it was opened by its own trigger or from outside (controlled `open`).
  const wasOpen = React.useRef(false);
  React.useEffect(() => {
    if (open && !wasOpen.current) {
      setFeatureId(presetFeatureId);
      setFinding(presetFinding);
    } else if (!open && wasOpen.current) {
      setFeatureId("");
      setDescription("");
      setFinding("");
      setErrors({});
      setError(null);
      setResult(null);
    }
    wasOpen.current = open;
  }, [open, presetFeatureId, presetFinding]);

  if (!canReportGap(epic)) return null;

  const repoFullName = githubRepoKey(epic.team?.gitRemote) ?? "";
  const canSubmit = !reportGap.isPending && target !== null;

  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const fieldErrors = validateReportGap({ featureId, description, finding });
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;
    setError(null);
    const trimmedFinding = finding.trim();
    try {
      const data = await reportGap.mutateAsync({
        featureId,
        description: description.trim(),
        ...(trimmedFinding ? { finding: trimmedFinding } : {}),
        target,
      });
      setResult(data);
    } catch (err) {
      setError(apiErrorInfo(err).message);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className={triggerClassName}>
          <MessageSquareWarning className="h-4 w-4 mr-2" />
          {label}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Report a gap</DialogTitle>
          <DialogDescription>
            Describe what this epic is missing. CompBox adds a follow-up task under the feature you choose and
            builds it.
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <>
            <ResultSummary result={result} />
            <DialogFooter>
              <Button onClick={() => { setOpen(false); }}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="report-gap-feature">Feature</Label>
                <Select value={featureId} onValueChange={setFeatureId}>
                  <SelectTrigger id="report-gap-feature" aria-label="Feature">
                    <SelectValue placeholder="Choose a feature" />
                  </SelectTrigger>
                  <SelectContent>
                    {features.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.identifier ? `${f.identifier} · ${f.title}` : f.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.featureId && <p className="text-xs text-destructive">{errors.featureId}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="report-gap-description">What's missing?</Label>
                <Textarea
                  id="report-gap-description"
                  value={description}
                  onChange={(e) => { setDescription(e.target.value); }}
                  placeholder="e.g. The export button doesn't include archived items"
                  rows={5}
                />
                {errors.description && <p className="text-xs text-destructive">{errors.description}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="report-gap-finding">Wrap-up finding (optional)</Label>
                <Textarea
                  id="report-gap-finding"
                  value={finding}
                  onChange={(e) => { setFinding(e.target.value); }}
                  placeholder="Paste the wrap-up finding this comes from, if any"
                  rows={3}
                />
                {errors.finding && <p className="text-xs text-destructive">{errors.finding}</p>}
              </div>
              <FixTargetPicker repoFullName={repoFullName} active={open} onChange={setTarget} />
              {error && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setOpen(false); }}>
                Cancel
              </Button>
              <Button onClick={() => { void handleSubmit(); }} disabled={!canSubmit}>
                {reportGap.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Report gap
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
